import React, { useState, useEffect } from 'react';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import {
    Box,
    TextField,
    Typography,
    Chip,
    Switch,
    IconButton,
    Autocomplete,
    Menu,
    MenuItem,
    Fade,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useDispatch } from 'react-redux';
import { setSelectedProfileSimulate } from '../../../../redux/simulateMgtSlice';

const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

const isEmptyAttributeValue = (value) => {
    if (Array.isArray(value)) {
        if (value.length === 0) {
            return true;
        }
        return value.every((item) => `${item ?? ''}`.trim() === '');
    }

    if (typeof value === 'boolean') {
        return false;
    }

    if (value === null || value === undefined) {
        return true;
    }

    if (typeof value === 'number') {
        return Number.isNaN(value);
    }

    return `${value}`.trim() === '';
};

export default function CustomProfilesSimulator({ profiles, onSaveProfile, onProfileChanged, onSelectedProfile, handleEditProfile, nodes, handleDeleteProfile }) {
    const [originalProfiles, setOriginalProfiles] = useState([]);
    const [draftProfiles, setDraftProfiles] = useState([]);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const dispatch = useDispatch();
    // const [isModified, setIsModified] = useState(false);
    const [modifiedItems, setModifiedItems] = useState({});
    const [expandedItems, setExpandedItems] = useState([]);
    const [profileEdit, setProfileEdit] = useState();

    useEffect(() => {
        if (profiles) {
            setOriginalProfiles(deepClone(profiles));
            setDraftProfiles(deepClone(profiles));
            setModifiedItems({});
            onProfileChanged?.(false);
        }
    }, [profiles]);

    const updateProfileModifiedState = (profileIndex, isModified) => {
        setModifiedItems((prev) => {
            const next = { ...prev, [profileIndex]: isModified };
            const hasAnyModified = Object.values(next).some(Boolean);
            onProfileChanged?.(hasAnyModified);
            return next;
        });
    };

    const handleValueChange = (profileIndex, attrKey, newValue) => {
        let newDrafts = [...draftProfiles];
        newDrafts[profileIndex] = {
            ...newDrafts[profileIndex],
            attributes: {
                ...newDrafts[profileIndex].attributes,
                [attrKey]: newValue
            }
        };
        const is_modified = JSON.stringify(newDrafts[profileIndex]) !== JSON.stringify(originalProfiles[profileIndex]);
        updateProfileModifiedState(profileIndex, is_modified);
        setDraftProfiles(newDrafts);
    };

    const handleSave = (e, index) => {
        e.stopPropagation();
        updateProfileModifiedState(index, false);
        const updatedProfile = {
            ...draftProfiles[index],
            updated_at: new Date().toISOString()
        };
        if (onSaveProfile) {
            onSaveProfile(updatedProfile);
        }
        setDraftProfiles((prev) => {
            const next = [...prev];
            next[index] = deepClone(updatedProfile);
            return next;
        });
        setOriginalProfiles((prev) => {
            const next = [...prev];
            next[index] = deepClone(updatedProfile);
            return next;
        });
    };

    const handleDiscard = (e, index) => {
        e.stopPropagation();
        updateProfileModifiedState(index, false);
        const newDrafts = [...draftProfiles];
        newDrafts[index] = deepClone(originalProfiles[index]);
        setDraftProfiles(newDrafts);
    };

    const handleMoreClick = (event, item) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setProfileEdit(item);
    };

    const onClose = () => {
        setAnchorEl(null);
    };

    const onProfileSelected = (event, itemId, isSelected) => {
        if (isSelected) {
            const selectedProfile = draftProfiles.find(profile => `${profile?.id}` === `${itemId}`) || null;
            dispatch(setSelectedProfileSimulate(selectedProfile));
            if (selectedProfile) {
                onSelectedProfile(selectedProfile);
            }
        }
    };

    const handleExpandedItemsChange = (event, itemIds) => {
        const newlyExpanded = itemIds.filter((id) => !expandedItems.includes(id));

        if (newlyExpanded.length > 0) {
            // ถ้ามีการกางอันใหม่ ให้บังคับเซ็ต State ให้มีแค่ ID ใหม่ตัวเดียว (ทำให้ตัวเก่าหุบทันที)
            setExpandedItems([newlyExpanded[0]]);
        } else {
            // ถ้าเป็นการคลิกเพื่อหุบตัวมันเอง (itemIds จะว่าง) ก็เซ็ตตามปกติ
            setExpandedItems(itemIds);
        }
    };

    const onClickMenu = (action) => {
        setAnchorEl(null);
        if (action === 'edit') {
            handleEditProfile(profileEdit);
        } else if (action === 'delete') {
            handleDeleteProfile(profileEdit);
        }
    }

    return (
        <SimpleTreeView
            onItemSelectionToggle={onProfileSelected}
            expandedItems={expandedItems}
            onExpandedItemsChange={handleExpandedItemsChange}
        >
            {draftProfiles.map((profile, index) => {
                const profileId = `${profile.id}`;
                const isCurrentItemModified = !!modifiedItems[index];
                return (
                    <TreeItem
                        key={profileId}
                        itemId={profileId}
                        sx={{
                            [`& .MuiTreeItem-content`]: {
                                borderRadius: 0,
                                height: '40px'
                            },
                        }}
                        label={
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    py: 1,
                                    width: '100%',
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    fontWeight="600"
                                    fontSize="medium"
                                >
                                    {profile.name}
                                </Typography>

                                <Box sx={{ display: "flex" }}>
                                    <Fade in={isCurrentItemModified} timeout={{ enter: 200, exit: 150 }} unmountOnExit>
                                        <Box sx={{ display: 'flex' }}>
                                            <IconButton
                                                aria-label="save"
                                                size="small"
                                                color="info"
                                                onClick={(e) => handleSave(e, index)}
                                            >
                                                <SaveIcon fontSize="inherit" />
                                            </IconButton>
                                            <IconButton
                                                aria-label="Discard"
                                                size="small"
                                                color="inherit"
                                                onClick={(e) => handleDiscard(e, index)}
                                            >
                                                <DoDisturbIcon fontSize="inherit" />
                                            </IconButton>
                                        </Box>
                                    </Fade>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => handleMoreClick(e, profile)}
                                        sx={{ '&:hover': { color: 'primary.main' } }}
                                    >
                                        <MoreVertIcon fontSize="inherit" />
                                    </IconButton>
                                </Box>
                            </Box>
                        }
                    >
                        {Object.entries(profile.attributes).map(
                            ([attrKey, attrValue]) => {
                                const isArray = Array.isArray(attrValue);
                                const valueType = isArray ? 'array' : typeof attrValue;
                                const fieldId = `${profileId}-${attrKey}`;
                                const hasError = isEmptyAttributeValue(attrValue);

                                return (
                                    <Box
                                        key={fieldId}
                                        // itemId={fieldId}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            px: 1,
                                            py: 0.5,
                                            ml: 3,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'text.secondary',
                                                fontWeight: 500,
                                            }}
                                        >
                                            {attrKey}:
                                        </Typography>

                                        {/* กรณี Array: ใช้ Autocomplete Multiple FreeSolo */}
                                        {isArray ? (
                                            <Autocomplete
                                                multiple
                                                freeSolo
                                                size='small'
                                                options={[]} // ไม่มี predefined options ให้พิมพ์เอาเอง
                                                value={attrValue}
                                                onClick={(e) => e.stopPropagation()}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onKeyDown={(e) => e.stopPropagation()}
                                                onChange={(event, newValue) =>
                                                    handleValueChange(index, attrKey, newValue)
                                                }
                                                renderValue={(tagValue, getTagProps) =>
                                                    tagValue.map((option, index) => {
                                                        const { key, ...tagProps } = getTagProps({ index });
                                                        return (
                                                            <Chip
                                                                label={option}
                                                                key={key}
                                                                {...tagProps}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        )
                                                    })
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        variant="outlined"
                                                        placeholder="Add items..."
                                                        error={hasError}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        onKeyDown={(e) => e.stopPropagation()}
                                                        sx={{
                                                            width: '100%',
                                                            fontSize: 12,
                                                            '& .MuiInputBase-input': {
                                                                padding: '0px 6px',
                                                                height: '28px',
                                                            },
                                                            '& .MuiInputBase-input::placeholder': {
                                                                fontSize: '12px',
                                                            },
                                                        }}
                                                    />
                                                )}
                                                sx={{ flexGrow: 1, width: 100 }}
                                            />
                                        ) : valueType === 'boolean' ? (
                                            <Switch
                                                size="small"
                                                checked={attrValue}
                                                onClick={(e) => e.stopPropagation()}
                                                onKeyDown={(e) => e.stopPropagation()}
                                                onChange={(e) =>
                                                    handleValueChange(index, attrKey, e.target.checked)
                                                }
                                            />
                                        ) : (
                                            <TextField
                                                size="small"
                                                variant="outlined"
                                                value={attrValue ?? ''}
                                                error={hasError}
                                                onClick={(e) => e.stopPropagation()}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onKeyDown={(e) => e.stopPropagation()}
                                                onChange={(e) => {
                                                    let val = e.target.value;
                                                    if (valueType === 'number')
                                                        val = val === '' ? '' : Number(val);
                                                    handleValueChange(index, attrKey, val);
                                                }}
                                                sx={{
                                                    width: '100%',
                                                    flex: 1,
                                                    '& .MuiInputBase-root': {
                                                        height: '28px',
                                                        fontSize: 12,
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        padding: '0px 6px',
                                                    },
                                                }}
                                            />
                                        )}

                                        {/* <Typography
                                                variant="caption"
                                                sx={{ color: '#ccc', fontWeight: 'bold' }}
                                            >
                                                {valueType.toUpperCase()}
                                            </Typography> */}
                                    </Box>
                                );
                            }
                        )}
                    </TreeItem>
                );
            })}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={onClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={() => onClickMenu('edit')}>Edit</MenuItem>
                <MenuItem onClick={() => onClickMenu('delete')}>Delete</MenuItem>
            </Menu>
        </SimpleTreeView>
    );
}